import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, map, mergeMap, of, tap, throwError } from 'rxjs';
import { NotificationService } from '../services/notification.service';
import { NotificationContext } from '../models/system-notification';
import { BACKGROUND_REQUEST, SKIP_SUCCESS_NOTIFICATION } from './background-request';
import { requestNotificationContext } from './notification-context';

function payload(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' ? value as Record<string, unknown> : {};
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

// Angular also returns JSON error bodies as Blobs for PDF download requests.
// Decode them before notifying and rethrowing so the page receives the same message.
function decodeDownloadError(error: HttpErrorResponse) {
  if (!(error.error instanceof Blob)) return of(error);
  return from(error.error.text()).pipe(
    map(raw => new HttpErrorResponse({
      error: JSON.parse(raw), headers: error.headers, status: error.status,
      statusText: error.statusText, url: error.url ?? undefined,
    })),
    catchError(() => of(error)),
  );
}

export const apiNotificationInterceptor: HttpInterceptorFn = (req, next) => {
  const notify = inject(NotificationService);
  const internalAuth = /\/(refresh-token|logout)(?:[/?]|$)/.test(req.url);
  return next(req).pipe(
    tap(event => {
      if (!(event instanceof HttpResponse) || req.context.get(BACKGROUND_REQUEST)
        || req.context.get(SKIP_SUCCESS_NOTIFICATION) || internalAuth
        || /\/login(?:[/?]|$)/.test(req.url) || !['POST', 'PUT', 'DELETE', 'PATCH'].includes(req.method)) return;
      const message = text(payload(event.body)['message']);
      if (message) notify.success(message, undefined, requestNotificationContext(req, event.status));
    }),
    catchError((error: HttpErrorResponse) => decodeDownloadError(error).pipe(
      mergeMap(decoded => {
        if (!req.context.get(BACKGROUND_REQUEST) && !internalAuth) {
          handleError(decoded, notify, requestNotificationContext(req, decoded.status), /\/login(?:[/?]|$)/.test(req.url));
        }
        return throwError(() => decoded);
      }),
    )),
  );
};

function handleError(error: HttpErrorResponse, notify: NotificationService, context: NotificationContext, login: boolean): void {
  const body = payload(error.error);
  const message = text(body['message']);
  const title = text(body['error']);
  const errors = Array.isArray(body['errors'])
    ? body['errors'].filter((value): value is string => typeof value === 'string')
    : Object.entries(payload(body['validationErrors']))
      .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
      .map(([field, value]) => `${field} : ${value}`);

  switch (error.status) {
    case 0:
      notify.error('Impossible de contacter le serveur. Vérifiez votre connexion ou réessayez plus tard.', [], 'Connexion impossible', context);
      break;
    case 400:
    case 409:
    case 422:
      notify.warning(message ?? 'La demande ne peut pas être validée. Vérifiez les informations saisies.', title ?? 'Attention', errors, context);
      break;
    case 401:
      notify.warning(login ? message ?? 'Vérifiez votre identifiant et votre mot de passe.'
        : 'Votre session a expiré. Veuillez vous reconnecter.', login ? 'Connexion refusée' : 'Session expirée', errors, context);
      break;
    case 403:
      notify.warning("Vous n’avez pas la permission d’effectuer cette action.", 'Accès refusé', errors, context);
      break;
    case 404:
      notify.warning(message ?? 'La ressource demandée est introuvable.', 'Introuvable', errors, context);
      break;
    case 429:
      notify.warning(message ?? 'Trop de demandes ont été envoyées. Patientez avant de réessayer.', 'Veuillez patienter', errors, context);
      break;
    default:
      notify.error(message ?? (error.status >= 500 ? 'Le service est momentanément indisponible. Réessayez plus tard.'
        : 'La demande n’a pas pu aboutir.'), errors, title ?? (error.status >= 500 ? 'Erreur serveur' : 'Erreur'), context);
  }
}
